import React from 'react'
import ReactModal from 'react-modal'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { DELETE_PREFILL_RECIPIENT } from '../../../../graphql/mutation'

type DeleteRecipientTarget = {
  recipientUuid: string
  recipientEmail: string
  recipientName?: string | null
}

interface Props {
  isOpen: boolean
  onClose: () => void
  recipient: DeleteRecipientTarget | null
  onDeleted: (recipientUuid: string) => void
}

const modalStyles: ReactModal.Styles = {
  overlay: { zIndex: 1060, backgroundColor: 'rgba(15, 23, 42, 0.45)' },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(440px, 92vw)',
    border: 'none',
    borderRadius: '14px',
    padding: '1.75rem',
    boxShadow: '0 20px 50px rgba(0,0,0, 0.15)'
  }
}

const DeletePrefillRecipientModal: React.FC<Props> = ({
  isOpen,
  onClose,
  recipient,
  onDeleted
}) => {
  const [deleteRecipient, { loading }] = useMutation(DELETE_PREFILL_RECIPIENT, {
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } }
  })

  const handleDelete = async () => {
    if (!recipient) return

    try {
      const { data: result } = await deleteRecipient({
        variables: { input: { recipientUuid: recipient.recipientUuid } }
      })
      if (result?.deletePrefillRecipient?.success) {
        toast.success('Recipient deleted')
        onDeleted(recipient.recipientUuid)
        onClose()
      } else {
        toast.error(result?.deletePrefillRecipient?.message || 'Delete failed')
      }
    } catch (err: any) {
      toast.error(err.message || 'Delete failed')
    }
  }

  const displayName =
    recipient?.recipientName?.trim() || recipient?.recipientEmail || 'this recipient'

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      style={modalStyles}
      contentLabel='Delete recipient confirmation'
    >
      <div className='d-flex align-items-start justify-content-between mb-3'>
        <div>
          <h5 className='fw-semibold mb-1'>Delete recipient</h5>
          <p className='text-muted small mb-0'>
            Remove <strong>{displayName}</strong> from this prefill batch?
          </p>
        </div>
        <button type='button' className='btn-close' onClick={onClose} aria-label='Close' />
      </div>

      <p className='small text-muted mb-4'>
        Their shareable form link will be removed. Any form submission data they already submitted
        will be kept.
      </p>

      <div className='d-flex justify-content-end gap-2'>
        <button
          type='button'
          className='btn btn-light btn-sm px-3'
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type='button'
          className='btn btn-danger btn-sm px-4'
          onClick={handleDelete}
          disabled={loading || !recipient}
        >
          {loading ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' role='status' />
              Deleting...
            </>
          ) : (
            <>
              <i className='bi bi-trash me-1' />
              Delete recipient
            </>
          )}
        </button>
      </div>
    </ReactModal>
  )
}

export default DeletePrefillRecipientModal
